import { z } from 'zod';
import { parseLocalDate } from '../local-date.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';
import {
  EQUIPMENT_CODE_MAX,
  EQUIPMENT_TAG_MAX,
  type EquipmentInput,
  type EquipmentNodeType,
  parseEquipmentInput,
} from './equipment.js';
import {
  INTERVAL_BASES,
  JOB_PRIORITIES,
  JOB_TYPES,
  type JobDefinitionInput,
  parseJobDefinitionInput,
  WORK_TYPES,
} from './jobs.js';
import {
  LIBRARY_DEPARTMENTS,
  LIBRARY_JOB_CODE_MAX,
  LIBRARY_LIMITS,
  type LibraryDepartment,
  libraryIntervalSchema,
} from './library.js';

/**
 * Kütüphaneden gemiye uygulama (M15f; ADR 0063 §6): sihirbazın ürettiği içerik ve planlayıcı.
 * İçerik kendi başınadır (gemi kütüphaneyi görmez); planlayıcı saf fonksiyondur: ofiste önizleme,
 * kurulum devrinde uygulama ve gemide kabul aynı planı kullanır. Hata varsa hiçbir şey yazılmaz.
 */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');
const text = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().min(1, 'REQUIRED').max(max, 'TOO_LONG'));
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const optionalLines = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform((value) =>
      value
        .normalize('NFC')
        .split(/\r\n|\r|\n/)
        .map(collapse)
        .filter((line) => line !== '')
        .join('\n'),
    )
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);
const flag = () =>
  z
    .boolean(typed('INVALID_OPTION'))
    .nullish()
    .transform((value) => value ?? false);
const option = <T extends readonly [string, ...string[]]>(values: T) =>
  z.enum(values, typed('INVALID_OPTION'));
const nullableOption = <T extends readonly [string, ...string[]]>(values: T) =>
  option(values)
    .nullish()
    .transform((value) => value ?? null);
const closed = <T extends z.core.$ZodLooseShape>(shape: T) =>
  z.strictObject(shape, {
    error: (issue) =>
      issue.code === 'unrecognized_keys'
        ? 'UNRECOGNIZED_KEY'
        : missing(issue.input)
          ? 'REQUIRED'
          : 'INVALID_OBJECT',
  });
const id = () => z.uuid(typed('INVALID_ID'));
const list = <T extends z.ZodType>(item: T, max: number) =>
  z
    .array(item, typed('INVALID_LIST'))
    .max(max, 'TOO_MANY')
    .nullish()
    .transform((value) => (value ?? []) as z.output<T>[]);
const localDate = z.string(typed('INVALID_DATE')).refine((value) => {
  try {
    parseLocalDate(value);
    return true;
  } catch {
    return false;
  }
}, 'INVALID_DATE');

export const LIBRARY_APPLICATION_LIMITS = { systems: 50, items: 1000, jobs: 5000 } as const;

const applicationSystemSchema = closed({
  libraryId: id(),
  /** Gemide grubun kodu (varsayılan sistem kodu; sihirbazda değiştirilebilir). */
  code: text(EQUIPMENT_CODE_MAX),
  name: text(LIBRARY_LIMITS.systemName),
});

const applicationItemSchema = closed({
  libraryId: id(),
  systemLibraryId: id(),
  code: text(EQUIPMENT_CODE_MAX),
  name: text(LIBRARY_LIMITS.itemName),
  tag: optionalText(EQUIPMENT_TAG_MAX),
  location: optionalText(120),
  critical: flag(),
  hasCounter: flag(),
  notes: optionalLines(LIBRARY_LIMITS.notes),
  maker: optionalText(LIBRARY_LIMITS.maker),
  model: optionalText(LIBRARY_LIMITS.model),
  /** Gemideki seri no (ekipman kartı sınırı; ortak 2026-10-31 "ekipmana özel bilgiler"). */
  serialNo: optionalText(80),
});

const applicationJobSchema = closed({
  libraryId: id(),
  itemLibraryId: id(),
  code: text(LIBRARY_JOB_CODE_MAX),
  title: text(LIBRARY_LIMITS.jobTitle),
  workType: option(WORK_TYPES),
  jobType: option(JOB_TYPES),
  priority: option(JOB_PRIORITIES),
  interval: libraryIntervalSchema,
  intervalBasis: nullableOption(INTERVAL_BASES),
  department: nullableOption(LIBRARY_DEPARTMENTS),
  manualRef: optionalText(LIBRARY_LIMITS.manualRef),
  baselineDoneOn: localDate.nullish().transform((value) => value ?? null),
  baselineDoneHours: z
    .number(typed('INVALID_NUMBER'))
    .min(0, 'OUT_OF_RANGE')
    .max(10_000_000, 'OUT_OF_RANGE')
    .nullish()
    .transform((value) => value ?? null),
});

/**
 * Uygulama içeriği (ADR 0063 §6): talepte ve sonuçta aynı içerik ve hash. Kimlikler tekil; kalemin
 * sistemi ve işin kalemi içerikte olmalı; en az bir kalem.
 */
export const libraryApplicationContentSchema = closed({
  systems: list(applicationSystemSchema, LIBRARY_APPLICATION_LIMITS.systems),
  items: list(applicationItemSchema, LIBRARY_APPLICATION_LIMITS.items),
  jobs: list(applicationJobSchema, LIBRARY_APPLICATION_LIMITS.jobs),
}).superRefine((content, ctx) => {
  const issue = (path: (string | number)[], message: string) =>
    ctx.addIssue({ code: 'custom', message, path });
  if (content.items.length === 0) issue(['items'], 'EMPTY_APPLICATION');
  const unique = (kind: 'systems' | 'items' | 'jobs', ids: string[]) => {
    const seen = new Set<string>();
    for (const [index, value] of ids.entries()) {
      if (seen.has(value)) issue([kind, index, 'libraryId'], 'DUPLICATE_ENTRY');
      seen.add(value);
    }
    return seen;
  };
  const systems = unique(
    'systems',
    content.systems.map((s) => s.libraryId),
  );
  const items = unique(
    'items',
    content.items.map((i) => i.libraryId),
  );
  unique(
    'jobs',
    content.jobs.map((j) => j.libraryId),
  );
  for (const [index, item] of content.items.entries()) {
    if (!systems.has(item.systemLibraryId)) {
      issue(['items', index, 'systemLibraryId'], 'UNKNOWN_SYSTEM');
    }
  }
  for (const [index, job] of content.jobs.entries()) {
    if (!items.has(job.itemLibraryId)) issue(['jobs', index, 'itemLibraryId'], 'ITEM_NOT_SELECTED');
  }
});
export type LibraryApplicationContent = z.output<typeof libraryApplicationContentSchema>;
export type LibraryApplicationSystem = LibraryApplicationContent['systems'][number];
export type LibraryApplicationItem = LibraryApplicationContent['items'][number];
export type LibraryApplicationJob = LibraryApplicationContent['jobs'][number];

export function parseLibraryApplicationContent(
  input: unknown,
): ParseResult<LibraryApplicationContent> {
  const result = libraryApplicationContentSchema.safeParse(input);
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/* ---------- Planlayıcı ---------- */

/** Gemi ağacının düğümü (silinmemişler). */
export interface ApplyTreeNode {
  id: string;
  parentId: string | null;
  nodeType: EquipmentNodeType;
  code: string;
  name: string;
  libraryId: string | null;
  tag: string | null;
  location: string | null;
  critical: boolean;
  hasCounter: boolean;
}

/** Geminin silinmemiş iş tanımı. */
export interface ApplyJobRef {
  id: string;
  code: string;
  libraryJobId: string | null;
}

export type LibraryApplyAction = 'existing' | 'adopt' | 'create';

export interface PlannedSystem {
  libraryId: string;
  code: string;
  name: string;
  action: LibraryApplyAction;
  /** Var olan ya da bağlanan düğüm; oluşturulacaksa `null`. */
  nodeId: string | null;
  error: string | null;
}

export interface PlannedItem {
  libraryId: string;
  systemLibraryId: string;
  code: string;
  name: string;
  action: LibraryApplyAction;
  nodeId: string | null;
  /** Bağlanan düğüme yazılacak alanlar (boş tag ve konum, kritik ve sayaç yalnız açılır). */
  updates: { tag?: string; location?: string; critical?: true; hasCounter?: true } | null;
  /** Oluşturulacak düğümün ekipman kartı. */
  equipment: EquipmentInput | null;
  error: string | null;
}

export interface PlannedJob {
  libraryId: string;
  itemLibraryId: string;
  code: string;
  title: string;
  action: 'existing' | 'create';
  jobId: string | null;
  /** Oluşturulacak iş tanımı; sayaç ve sorumlu rol yazma anında çözülür. */
  definition: JobDefinitionInput | null;
  department: LibraryDepartment | null;
  error: string | null;
  field: string | null;
}

export interface LibraryApplicationPlan {
  systems: PlannedSystem[];
  items: PlannedItem[];
  jobs: PlannedJob[];
  errors: {
    kind: 'system' | 'item' | 'job';
    libraryId: string;
    code: string;
    field: string | null;
  }[];
  summary: {
    groupsCreated: number;
    groupsExisting: number;
    itemsCreated: number;
    itemsAdopted: number;
    itemsExisting: number;
    jobsCreated: number;
    jobsExisting: number;
  };
}

const keyOf = (value: string) => value.normalize('NFC').trim().toLocaleLowerCase('en');

/**
 * Planlayıcı (ADR 0063 §6):
 * - Sistem: `library_id`'si o sistem olan düğüm var; yoksa aynı kodlu ve adlı kök grup bağlanır;
 *   yoksa kod boşsa grup oluşturulur; kod başkasındaysa `GROUP_CODE_IN_USE`.
 * - Kalem: `library_id`'si o kalem olan düğüm var (dokunulmaz); yoksa sistem düğümünün doğrudan
 *   altında aynı kodlu düğüm bağlanır; yoksa kod boşsa ekipman oluşturulur; kod başkasındaysa
 *   `EQUIPMENT_CODE_IN_USE`. Tag gemide tekil (`DUPLICATE_EQUIPMENT_TAG`).
 * - İş: `library_job_id`'si o iş olan tanım var (atlanır); kod gemide varsa `JOB_CODE_IN_USE`;
 *   aralıksız iş gemiye inmez (`INTERVAL_REQUIRED`) [NR496 4.4.2a]; dayanak zorunlu
 *   (`INTERVAL_BASIS_REQUIRED`) [UR Z20 1.2.1]; saat aralığı sayaç ister (`COUNTER_REQUIRED`, K6),
 *   sayaç yeni ise başlangıç da (`BASELINE_HOURS_REQUIRED`, K12). İş tanımı şeması da uygulanır.
 * `today` gemi yerel günüdür (başlangıç günü gelecekte olamaz).
 */
export function planLibraryApplication(
  content: LibraryApplicationContent,
  vessel: { nodes: readonly ApplyTreeNode[]; jobs: readonly ApplyJobRef[] },
  options: { today: string },
): LibraryApplicationPlan {
  const byId = new Map(vessel.nodes.map((n) => [n.id, n]));
  const byLibraryId = new Map(
    vessel.nodes.filter((n) => n.libraryId !== null).map((n) => [n.libraryId as string, n]),
  );
  const usedCodes = new Set(vessel.nodes.map((n) => keyOf(n.code)));
  const usedTags = new Map<string, string>();
  for (const node of vessel.nodes) if (node.tag) usedTags.set(keyOf(node.tag), node.id);
  const errors: LibraryApplicationPlan['errors'] = [];

  const systems: PlannedSystem[] = content.systems.map((system) => {
    const planned: PlannedSystem = {
      libraryId: system.libraryId,
      code: system.code,
      name: system.name,
      action: 'create',
      nodeId: null,
      error: null,
    };
    const linked = byLibraryId.get(system.libraryId);
    if (linked) return { ...planned, action: 'existing', nodeId: linked.id };
    const sameCode = vessel.nodes.find((n) => keyOf(n.code) === keyOf(system.code));
    if (
      sameCode &&
      sameCode.parentId === null &&
      sameCode.nodeType === 'group' &&
      sameCode.libraryId === null &&
      keyOf(sameCode.name) === keyOf(system.name)
    ) {
      return { ...planned, action: 'adopt', nodeId: sameCode.id };
    }
    if (usedCodes.has(keyOf(system.code))) return { ...planned, error: 'GROUP_CODE_IN_USE' };
    usedCodes.add(keyOf(system.code));
    return planned;
  });
  const systemOf = new Map(systems.map((s) => [s.libraryId, s]));

  /** Düğüm ya da atalarından biri sayaçlı mı (K6). */
  const counterAbove = (nodeId: string | null): boolean => {
    const seen = new Set<string>();
    let cursor = nodeId === null ? undefined : byId.get(nodeId);
    while (cursor && !seen.has(cursor.id)) {
      if (cursor.hasCounter) return true;
      seen.add(cursor.id);
      cursor = cursor.parentId === null ? undefined : byId.get(cursor.parentId);
    }
    return false;
  };

  /** Kalemin sayacı: var olan (önceden) ya da bu uygulamayla yeni. */
  const counterOfItem = new Map<string, 'existing' | 'new' | 'none'>();

  const items: PlannedItem[] = content.items.map((item) => {
    const system = systemOf.get(item.systemLibraryId);
    const planned: PlannedItem = {
      libraryId: item.libraryId,
      systemLibraryId: item.systemLibraryId,
      code: item.code,
      name: item.name,
      action: 'create',
      nodeId: null,
      updates: null,
      equipment: null,
      error: null,
    };
    const linked = byLibraryId.get(item.libraryId);
    if (linked) {
      counterOfItem.set(item.libraryId, counterAbove(linked.id) ? 'existing' : 'none');
      return { ...planned, action: 'existing', nodeId: linked.id };
    }
    const parentId = system?.nodeId ?? null;
    const sameCode = vessel.nodes.find((n) => keyOf(n.code) === keyOf(item.code));
    const tagError = (selfId: string | null) => {
      if (!item.tag) return null;
      const owner = usedTags.get(keyOf(item.tag));
      if (owner !== undefined && owner !== selfId) return 'DUPLICATE_EQUIPMENT_TAG';
      usedTags.set(keyOf(item.tag), selfId ?? `new:${item.libraryId}`);
      return null;
    };
    if (
      sameCode &&
      parentId !== null &&
      sameCode.parentId === parentId &&
      sameCode.libraryId === null &&
      sameCode.nodeType !== 'group'
    ) {
      const updates: NonNullable<PlannedItem['updates']> = {};
      if (item.tag && !sameCode.tag) updates.tag = item.tag;
      if (item.location && !sameCode.location) updates.location = item.location;
      if (item.critical && !sameCode.critical) updates.critical = true;
      if (item.hasCounter && !sameCode.hasCounter) updates.hasCounter = true;
      const before = counterAbove(sameCode.id);
      counterOfItem.set(item.libraryId, before ? 'existing' : item.hasCounter ? 'new' : 'none');
      const error = updates.tag ? tagError(sameCode.id) : null;
      return { ...planned, action: 'adopt', nodeId: sameCode.id, updates, error };
    }
    if (usedCodes.has(keyOf(item.code))) return { ...planned, error: 'EQUIPMENT_CODE_IN_USE' };
    usedCodes.add(keyOf(item.code));
    counterOfItem.set(
      item.libraryId,
      item.hasCounter ? 'new' : counterAbove(parentId) ? 'existing' : 'none',
    );
    const parsed = parseEquipmentInput({
      nodeType: 'equipment',
      code: item.code,
      name: item.name,
      tag: item.tag,
      location: item.location,
      critical: item.critical,
      hasCounter: item.hasCounter,
      notes: item.notes,
      maker: item.maker,
      model: item.model,
      serialNo: item.serialNo,
    });
    if (!parsed.ok) return { ...planned, error: parsed.issues[0]?.code ?? 'INVALID_EQUIPMENT' };
    return { ...planned, equipment: parsed.value, error: tagError(null) };
  });

  const linkedJobs = new Map(
    vessel.jobs.filter((j) => j.libraryJobId !== null).map((j) => [j.libraryJobId as string, j]),
  );
  const jobCodes = new Set(vessel.jobs.map((j) => keyOf(j.code)));
  const jobs: PlannedJob[] = content.jobs.map((job) => {
    const planned: PlannedJob = {
      libraryId: job.libraryId,
      itemLibraryId: job.itemLibraryId,
      code: job.code,
      title: job.title,
      action: 'create',
      jobId: null,
      definition: null,
      department: job.department,
      error: null,
      field: null,
    };
    const linked = linkedJobs.get(job.libraryId);
    if (linked) return { ...planned, action: 'existing', jobId: linked.id };
    const fail = (error: string, field: string | null) => ({ ...planned, error, field });
    if (jobCodes.has(keyOf(job.code))) return fail('JOB_CODE_IN_USE', 'code');
    jobCodes.add(keyOf(job.code));
    if (job.interval.calendar === null && job.interval.runningHours === null) {
      return fail('INTERVAL_REQUIRED', 'interval');
    }
    if (job.intervalBasis === null) return fail('INTERVAL_BASIS_REQUIRED', 'intervalBasis');
    if (job.interval.runningHours !== null) {
      const counter = counterOfItem.get(job.itemLibraryId) ?? 'none';
      if (counter === 'none') return fail('COUNTER_REQUIRED', 'interval');
      if (counter === 'new' && (job.baselineDoneOn === null || job.baselineDoneHours === null)) {
        return fail('BASELINE_HOURS_REQUIRED', 'baselineDoneHours');
      }
    }
    const parsed = parseJobDefinitionInput(
      {
        code: job.code,
        title: job.title,
        workType: job.workType,
        jobType: job.jobType,
        priority: job.priority,
        interval: {
          calendar: job.interval.calendar,
          runningHours: job.interval.runningHours,
          conditionPointIds: [],
        },
        intervalBasis: job.intervalBasis,
        manualRef: job.manualRef,
        baselineDoneOn: job.baselineDoneOn,
        baselineDoneHours: job.baselineDoneHours,
      },
      { today: options.today },
    );
    if (!parsed.ok) {
      const first = parsed.issues[0];
      return fail(first?.code ?? 'INVALID_JOB', first?.path || null);
    }
    return { ...planned, definition: parsed.value };
  });

  for (const s of systems) {
    if (s.error)
      errors.push({ kind: 'system', libraryId: s.libraryId, code: s.error, field: 'code' });
  }
  for (const i of items) {
    if (i.error) {
      errors.push({
        kind: 'item',
        libraryId: i.libraryId,
        code: i.error,
        field: i.error === 'DUPLICATE_EQUIPMENT_TAG' ? 'tag' : 'code',
      });
    }
  }
  for (const j of jobs) {
    if (j.error)
      errors.push({ kind: 'job', libraryId: j.libraryId, code: j.error, field: j.field });
  }
  const count = <T extends { action: string }>(rows: T[], action: string) =>
    rows.filter((r) => r.action === action && !('error' in r && r.error)).length;
  return {
    systems,
    items,
    jobs,
    errors,
    summary: {
      groupsCreated: count(systems, 'create'),
      groupsExisting: count(systems, 'existing') + count(systems, 'adopt'),
      itemsCreated: count(items, 'create'),
      itemsAdopted: count(items, 'adopt'),
      itemsExisting: count(items, 'existing'),
      jobsCreated: count(jobs, 'create'),
      jobsExisting: count(jobs, 'existing'),
    },
  };
}
